import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiCalendar } from 'react-icons/hi';

interface DateRangePickerProps {
  dateFrom: string;
  dateTo: string;
  onChange: (dateFrom: string, dateTo: string) => void;
}

type PresetType = 'today' | 'yesterday' | 'last7Days' | 'last30Days' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'lastQuarter' | 'custom';

// Helper function to compare dates by day only (ignore time)
const isSameDay = (date1: Date, date2: Date): boolean => {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

// Helper function to get date range for a preset
const getDateRangeForPreset = (preset: PresetType): { from: Date; to: Date } => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (preset) {
    case 'today':
      return {
        from: new Date(today),
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
      };
    
    case 'yesterday':
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        from: yesterday,
        to: new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999)
      };
    
    case 'last7Days':
      const last7Days = new Date(today);
      last7Days.setDate(today.getDate() - 7);
      return {
        from: last7Days,
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
      };
    
    case 'last30Days':
      const last30Days = new Date(today);
      last30Days.setDate(today.getDate() - 30);
      return {
        from: last30Days,
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
      };
    
    case 'thisWeek':
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Monday
      return {
        from: startOfWeek,
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
      };
    
    case 'lastWeek':
      const lastWeekStart = new Date(today);
      lastWeekStart.setDate(today.getDate() - today.getDay() - 6); // Last Monday
      const lastWeekEnd = new Date(lastWeekStart);
      lastWeekEnd.setDate(lastWeekStart.getDate() + 6); // Last Sunday
      return {
        from: lastWeekStart,
        to: new Date(lastWeekEnd.getFullYear(), lastWeekEnd.getMonth(), lastWeekEnd.getDate(), 23, 59, 59, 999)
      };
    
    case 'thisMonth':
      return {
        from: new Date(today.getFullYear(), today.getMonth(), 1),
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
      };
    
    case 'lastMonth':
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        from: lastMonth,
        to: new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), lastMonthEnd.getDate(), 23, 59, 59, 999)
      };
    
    case 'thisQuarter':
      const currentQuarter = Math.floor(today.getMonth() / 3);
      const quarterStart = new Date(today.getFullYear(), currentQuarter * 3, 1);
      return {
        from: quarterStart,
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
      };
    
    case 'lastQuarter':
      const lastQuarterNum = Math.floor(today.getMonth() / 3) - 1;
      const lastQuarterYear = lastQuarterNum < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const lastQuarterMonth = lastQuarterNum < 0 ? 9 : lastQuarterNum * 3;
      const lastQuarterStart = new Date(lastQuarterYear, lastQuarterMonth, 1);
      const lastQuarterEnd = new Date(lastQuarterYear, lastQuarterMonth + 3, 0);
      return {
        from: lastQuarterStart,
        to: new Date(lastQuarterEnd.getFullYear(), lastQuarterEnd.getMonth(), lastQuarterEnd.getDate(), 23, 59, 59, 999)
      };
    
    default:
      return { from: today, to: today };
  }
};

// Detect preset from date range (only used for initial detection)
const detectPresetFromDates = (dateFrom: string, dateTo: string): PresetType => {
  const from = new Date(dateFrom);
  const to = new Date(dateTo);
  
  // Order: check broader ranges first to prefer "thisMonth" over "today" when both match
  const presetKeys: PresetType[] = ['thisMonth', 'lastMonth', 'thisQuarter', 'lastQuarter', 'thisWeek', 'lastWeek', 'last30Days', 'last7Days', 'yesterday', 'today'];
  
  for (const preset of presetKeys) {
    const range = getDateRangeForPreset(preset);
    if (isSameDay(from, range.from) && isSameDay(to, range.to)) {
      return preset;
    }
  }
  
  return 'custom';
};

const DateRangePicker: React.FC<DateRangePickerProps> = ({ dateFrom, dateTo, onChange }) => {
  const { t } = useTranslation();
  
  // Detect initial preset only once on mount
  const [selectedPreset, setSelectedPreset] = useState<PresetType>(() => 
    detectPresetFromDates(dateFrom, dateTo)
  );
  const [showCustom, setShowCustom] = useState(false);
  const [customFrom, setCustomFrom] = useState(dateFrom.split('T')[0]);
  const [customTo, setCustomTo] = useState(dateTo.split('T')[0]);

  // Sync custom date inputs with props
  React.useEffect(() => {
    setCustomFrom(dateFrom.split('T')[0]);
    setCustomTo(dateTo.split('T')[0]);
  }, [dateFrom, dateTo]);

  // Sync selectedPreset when props change (e.g., when parent component resets dates)
  React.useEffect(() => {
    const detectedPreset = detectPresetFromDates(dateFrom, dateTo);
    setSelectedPreset(detectedPreset);
    if (detectedPreset !== 'custom') {
      setShowCustom(false);
    }
  }, [dateFrom, dateTo]);

  const handlePresetClick = (preset: PresetType) => {
    setSelectedPreset(preset);
    
    if (preset === 'custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      const range = getDateRangeForPreset(preset);
      onChange(range.from.toISOString(), range.to.toISOString());
    }
  };

  const handleCustomApply = () => {
    if (customFrom && customTo) {
      const from = new Date(customFrom);
      from.setHours(0, 0, 0, 0);
      const to = new Date(customTo);
      to.setHours(23, 59, 59, 999);
      onChange(from.toISOString(), to.toISOString());
      setShowCustom(false);
      setSelectedPreset('custom');
    }
  };

  const formatDisplayDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const presets: { key: PresetType; label: string }[] = [
    { key: 'today', label: t('dateRange.today') },
    { key: 'yesterday', label: t('dateRange.yesterday') },
    { key: 'last7Days', label: t('dateRange.last7Days') },
    { key: 'last30Days', label: t('dateRange.last30Days') },
    { key: 'thisWeek', label: t('dateRange.thisWeek') },
    { key: 'lastWeek', label: t('dateRange.lastWeek') },
    { key: 'thisMonth', label: t('dateRange.thisMonth') },
    { key: 'lastMonth', label: t('dateRange.lastMonth') },
    { key: 'thisQuarter', label: t('dateRange.thisQuarter') },
    { key: 'lastQuarter', label: t('dateRange.lastQuarter') },
    { key: 'custom', label: t('dateRange.custom') },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-sm text-gray-700">
        <HiCalendar className="h-4 w-4" />
        <span className="font-medium">{t('dateRange.selectPeriod')}:</span>
        
        {/* Display selected date range - always visible */}
        <div className="text-sm text-gray-600 font-medium">
          {formatDisplayDate(dateFrom)} - {formatDisplayDate(dateTo)}
        </div>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {presets.map((preset) => (
          <button
            key={preset.key}
            onClick={() => handlePresetClick(preset.key)}
            className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
              selectedPreset === preset.key
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>

      {showCustom && (
        <div className="p-4 bg-gray-50 rounded-lg space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('dateRange.from', 'Từ ngày')}
              </label>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('dateRange.to', 'Đến ngày')}
              </label>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <button
            onClick={handleCustomApply}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            {t('common.apply', 'Áp dụng')}
          </button>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
